import React, { useEffect, useState } from "react";
import {
  Table,
  Tabs,
  Card,
  Typography,
  Spin,
  Button,
  Tag,
  Empty,
  Tooltip,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useAccount } from "wagmi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/global-background.css";
import "../styles/LeaderboardPage.css";
import { Line } from "@ant-design/plots";
import { api } from "@/api/api";
import {
  TrophyOutlined,
  FireOutlined,
  ThunderboltOutlined,
  RiseOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// 排行榜接口类型
interface LeaderboardEntry {
  wallet: string; // 玩家钱包地址
  totalMmr: number; // 当前总积分
}

interface LeaderboardResponse {
  success: boolean; // true
  message: string; // 描述信息
  top: LeaderboardEntry[]; // 排名前 N 名列表
}

// 对局历史接口类型
interface MatchHistoryEntry {
  matchId: string;
  mode: string;
  startTime: string;
  endTime: string;
  result: "win" | "lose" | "draw";
  mmrBefore: number;
  mmrAfter: number;
  mmrDelta: number;

  damage?: number;
  damaged?: number;
  draw?: number;
  play?: number;
  kills?: number;
}

interface MatchHistoryResponse {
  success: boolean; // true
  message: string; // 描述信息
  history: MatchHistoryEntry[]; // 按时间倒序的最近 N 局列表
}

// 单局战绩接口类型
interface StatsPayload {
  wallet: string; // 玩家钱包
  damage: number; // 造成的总伤害
  damaged: number; // 承受的总伤害
  draw: number; // 摸牌数
  play: number; // 出牌数
  kills: number; // 击杀数
  result: "win" | "lose" | "draw"; // 胜负
  endTime: string; // 结束时间 ISO8601
  mmrDelta: number; // 本局积分增减
  totalMmr: number; // 本局后总积分
}

interface GetMatchStatsResponse {
  success: boolean; // true
  message: string; // 描述信息
  stats: StatsPayload[]; // 本局所有玩家的详细战绩数组
}

interface PlayerRanking {
  rank: number;
  wallet: string;
  totalMmr: number;
  displayWallet?: string;
}

interface MatchRecord {
  matchId: string;
  mode: string;
  startTime: string | number;
  endTime: string | number;
  result: "win" | "lose" | "draw";
  mmrBefore?: number;
  mmrAfter?: number;
  mmrDelta: number;
  totalMmr?: number;
  damage?: number;
  damaged?: number;
  kills?: number;
}

// CustomModal 接口定义
interface CustomModalProps {
  visible: boolean;
  onCancel: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  width?: number;
}

// CustomModal 组件
const CustomModal: React.FC<CustomModalProps> = ({
  visible,
  onCancel,
  title,
  children,
  width = 800,
}) => {
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleEscKey);
      // 防止背景滚动
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [visible, onCancel]);

  if (!visible) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div 
      className="custom-modal-backdrop"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div 
        className="ant-modal-content"
        style={{
          width: `${width}px`,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div className="ant-modal-header" style={{ position: 'relative' }}>
          <div className="ant-modal-title">{title}</div>
          <button 
            className="ant-modal-close"
            onClick={onCancel}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              border: 'none',
              background: 'transparent',
              fontSize: '16px',
              cursor: 'pointer',
              color: '#fff',
              width: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span className="ant-modal-close-x">×</span>
          </button>
        </div>
        <div className="ant-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

const LeaderboardPage: React.FC = () => {
  const [language, setLanguage] = useState<"en" | "zh">("zh");
  const [loading, setLoading] = useState(true);
  const [matchLoading, setMatchLoading] = useState(false);
  const [rankings, setRankings] = useState<PlayerRanking[]>([]);
  const [playerMatches, setPlayerMatches] = useState<MatchRecord[]>([]);
  const [currentWallet, setCurrentWallet] = useState<string | null>(null);
  const [currentPlayerRank, setCurrentPlayerRank] = useState<number | null>(
    null
  );
  const { address } = useAccount();
  const navigate = useNavigate();
  // Modal 状态
  const [modalVisible, setModalVisible] = useState(false);
  const [modalWallet, setModalWallet] = useState<string | null>(null);
  const [modalMatches, setModalMatches] = useState<MatchRecord[]>([]);
  const [modalLoading, setModalLoading] = useState(false);

  // 获取排行榜数据，并可手动刷新
  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await api.get("/cryptokill/matches/leaderboard?limit=50");
      const data = response.data as LeaderboardResponse;

      if (data.success && data.top) {
        const rankedData = data.top.map((entry, index) => ({
          rank: index + 1,
          wallet: entry.wallet,
          displayWallet: `${entry.wallet.substring(0, 6)}...${entry.wallet.substring(entry.wallet.length - 4)}`,
          totalMmr: entry.totalMmr,
        }));

        setRankings(rankedData);

        if (address) {
          const userEntry = rankedData.find(item => item.wallet.toLowerCase() === address.toLowerCase());

          // 无论用户是否在排行榜中，都设置当前钱包并拉取对局记录
          setCurrentWallet(address);
          fetchPlayerMatches(address);

          if (userEntry) {
            setCurrentPlayerRank(userEntry.rank);
          } else {
            setCurrentPlayerRank(-1);
          }
        }
      } else {
        console.error("获取排行榜数据失败:", data.message);
      }
    } catch (error) {
      console.error("获取排行榜失败:", error);
      if (address) {
        const userRank = Math.floor(Math.random() * 100) + 1;
        setCurrentPlayerRank(userRank);
        setCurrentWallet(address);
        fetchPlayerMatches(address);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [address]);

  // 获取玩家对局记录（页面级，用于当前登录玩家）
  const fetchPlayerMatches = async (wallet: string) => {
    if (!wallet) return;
    setMatchLoading(true);
    try {
      const response = await api.get<MatchHistoryResponse>(
        `/cryptokill/matches?wallet=${wallet}`
      );
      const data = response.data;
      if (!data.success) {
        console.error("获取对局历史失败:", data.message);
        setPlayerMatches([]);
      } else {
        const history = data.history || [];
        const records: MatchRecord[] = history.map((match) => ({
          matchId: match.matchId,
          mode: match.mode,
          startTime: match.startTime,
          endTime: match.endTime,
          result: match.result,
          mmrBefore: match.mmrBefore,
          mmrAfter: match.mmrAfter,
          mmrDelta: match.mmrDelta,
          damage: match.damage,
          damaged: match.damaged,
          draw: match.draw,
          play: match.play,
          kills: match.kills,
        }));
        setPlayerMatches(records);
        // 不打印错误，即使 history 为空
      }
    } catch (error) {
      console.error("获取玩家对局记录失败:", error);
      setPlayerMatches([]);
    } finally {
      setMatchLoading(false);
    }
  };

  // 弹窗查看指定玩家对局记录
  const fetchMatchesForModal = async (wallet: string) => {
    setModalLoading(true);
    try {
      const response = await api.get<MatchHistoryResponse>(
        `/cryptokill/matches?wallet=${wallet}`
      );
      const data = response.data;
      console.log(data);
      if (!data.success) {
        console.error("获取对局历史失败:", data.message);
        setModalMatches([]);
      } else {
        const history = data.history || [];
        const records: MatchRecord[] = history.map((match) => ({
          matchId: match.matchId,
          mode: match.mode,
          startTime: match.startTime,
          endTime: match.endTime,
          result: match.result,
          mmrBefore: match.mmrBefore,
          mmrAfter: match.mmrAfter,
          mmrDelta: match.mmrDelta,
          damage: match.damage,
          damaged: match.damaged,
          draw: match.draw,
          play: match.play,
          kills: match.kills,
        }));
        setModalMatches(records);
        // 不打印错误，即使 history 为空
      }
    } catch (error) {
      console.error("获取对局历史失败:", error);
      setModalMatches([]);
    } finally {
      setModalLoading(false);
    }
  };

  const handleViewPlayerMatches = (wallet: string) => {
    setModalWallet(wallet);
    fetchMatchesForModal(wallet);
    setModalVisible(true);
  };

  // 排行榜表格列定义
  const rankingColumns = [
    {
      title: language === "en" ? "Rank" : "排名",
      dataIndex: "rank",
      key: "rank",
      width: 100,
      render: (rank: number) => (
        <div className="rank-cell">
          {rank <= 3 ? (
            <div className={`top-rank top-${rank}`}>
              {rank === 1 && <TrophyOutlined />}
              {rank}
            </div>
          ) : (
            <span>{rank}</span>
          )}
        </div>
      ),
    },
    {
      title: language === "en" ? "Player" : "玩家",
      dataIndex: "wallet",
      key: "player",
      render: (_text: string, record: PlayerRanking) => (
        <Button
          type="link"
          onClick={() => handleViewPlayerMatches(record.wallet)}
          className={currentWallet === record.wallet ? "selected-player" : ""}
        >
          {record.wallet}
          {address && address.toLowerCase() === record.wallet.toLowerCase() && (
            <Tag color="gold" className="player-tag">
              {language === "en" ? "YOU" : "你"}
            </Tag>
          )}
        </Button>
      ),
    },
    {
      title: language === "en" ? "MMR" : "积分",
      dataIndex: "totalMmr",
      key: "mmr",
      render: (mmr: number) => <div className="mmr-value">{mmr}</div>,
    },
    {
      title: language === "en" ? "Actions" : "操作",
      key: "actions",
      width: 120,
      render: (_: any, record: PlayerRanking) => (
        <Button
          type="primary"
          size="small"
          ghost
          onClick={() => handleViewPlayerMatches(record.wallet)}
          className="view-button"
        >
          {language === "en" ? "View" : "查看"}
        </Button>
      ),
    },
  ];

  // 对局记录表格列定义
  const matchColumns = [
    {
      title: language === "en" ? "Time" : "时间",
      dataIndex: "startTime",
      key: "time",
      width: 120,
      render: (time: string | number, record: MatchRecord) => {
        try {
          const startDate = new Date(record.startTime);
          const endDate = new Date(record.endTime);
          
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return "-";
          }
          
          const startStr = startDate.toLocaleString('zh-CN', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
          
          const endStr = endDate.toLocaleString('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
          });
          
          return (
            <Tooltip title={`${startDate.toLocaleString()} - ${endDate.toLocaleString()}`}>
              <div>
                <div>{startStr}</div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  至 {endStr}
                </div>
              </div>
            </Tooltip>
          );
        } catch (error) {
          return "-";
        }
      },
    },
    {
      title: language === "en" ? "Mode" : "模式",
      dataIndex: "mode",
      key: "mode",
      width: 80,
      render: (mode: string) => {
        const modeText = mode === "versus" ? 
          (language === "en" ? "Ranked" : "排位") : 
          (language === "en" ? "Casual" : "匹配");
        return <span>{modeText}</span>;
      },
    },
    {
      title: language === "en" ? "Result" : "结果",
      dataIndex: "result",
      key: "result",
      width: 80,
      render: (result: string) => {
        let color = "default";
        let text = result;

        if (result === "win") {
          color = "success";
          text = language === "en" ? "Win" : "胜利";
        } else if (result === "lose") {
          color = "error";
          text = language === "en" ? "Lose" : "失败";
        } else {
          color = "warning";
          text = language === "en" ? "Draw" : "平局";
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: language === "en" ? "MMR Change" : "积分变化",
      dataIndex: "mmrDelta",
      key: "mmrDelta",
      width: 100,
      render: (delta: number) => {
        const isPositive = delta > 0;
        return (
          <Text className={isPositive ? "positive-mmr" : "negative-mmr"}>
            {isPositive ? "+" : ""}
            {delta}
          </Text>
        );
      },
    },
    {
      title: language === "en" ? "MMR After" : "对局后积分",
      dataIndex: "mmrAfter",
      key: "mmrAfter",
      width: 100,
      render: (mmr: number) => <span>{mmr}</span>,
    },
    {
      title: language === "en" ? "Kills" : "击杀",
      dataIndex: "kills",
      key: "kills",
      width: 60,
      render: (kills: number) => <span>{kills || "-"}</span>,
    },
    {
      title: language === "en" ? "Damage" : "造成伤害",
      dataIndex: "damage",
      key: "damage",
      width: 80,
      render: (damage: number) => <span>{damage || "-"}</span>,
    },
    {
      title: language === "en" ? "Damaged" : "受到伤害",
      dataIndex: "damaged",
      key: "damaged",
      width: 80,
      render: (damaged: number) => <span>{damaged || "-"}</span>,
    },
    {
      title: language === "en" ? "Cards Drawn" : "摸牌数",
      dataIndex: "draw",
      key: "draw",
      width: 80,
      render: (draw: number) => <span>{draw || "-"}</span>,
    },
    {
      title: language === "en" ? "Cards Played" : "出牌数",
      dataIndex: "play",
      key: "play",
      width: 80,
      render: (play: number) => <span>{play || "-"}</span>,
    },
  ];

  // 生成MMR走势图数据
  const getMmrChartData = () => {
    // 使用mmrAfter数据绘制走势图，如果有的话
    return playerMatches
      .slice() // 创建副本
      .reverse() // 时间升序
      .map((match, index) => ({
        match: index + 1,
        mmr: match.mmrAfter || match.totalMmr || 0,
      }));
  };

  return (
    <div className="nova-container">
      <div className="nova-grid"></div>

      <Navbar language={language} />

      <div className="content">
        <div className="title-section">
          <h1 className="nova-title">
            {language === "en" ? "Leaderboard" : "玩家排行榜"}
          </h1>
          <p className="nova-subtitle">
            {language === "en"
              ? "Top players from Crypto Battle Arena"
              : "币圈大乱斗玩家排名"}
          </p>
          <Button
            className="refresh-button"
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchLeaderboard}
            loading={loading}
          >
            {language === "en" ? "Refresh" : "刷新"}
          </Button>
        </div>
        <div className="leaderboard-container">
          <Tabs defaultActiveKey="ranking" className="leaderboard-tabs">
            <TabPane
              tab={
                <span className="tab-label">
                  <TrophyOutlined />
                  {language === "en" ? "Global Ranking" : "玩家排行榜"}
                </span>
              }
              key="ranking"
            >
              <div className="leaderboard-card">
                {address && currentPlayerRank && (
                  <div className="player-rank-card">
                    <div className="player-rank-info">
                      <RiseOutlined className="rank-icon" />
                      <div className="player-rank-text">
                        <Text className="player-rank-label">
                          {language === "en" ? "Your Rank" : "你的排名"}
                        </Text>
                        <Title level={2} className="player-rank-value">
                          #{currentPlayerRank}
                        </Title>
                      </div>
                    </div>
                    <div className="player-rank-mmr">
                      <Text className="player-mmr-label">
                        {language === "en" ? "Your MMR" : "你的MMR"}
                      </Text>
                      <Title level={3} className="player-mmr-value">
                        {rankings.find((r) => r.rank === currentPlayerRank)
                          ?.totalMmr || 0}
                      </Title>
                    </div>
                  </div>
                )}

                <Table
                  columns={rankingColumns}
                  dataSource={rankings}
                  rowKey="rank"
                  loading={loading}
                  pagination={{ pageSize: 20 }}
                  className="leaderboard-table"
                  rowClassName={(record) =>
                    record.wallet === currentWallet ? "selected-row" : ""
                  }
                />
              </div>
            </TabPane>

            <TabPane
              tab={
                <span className="tab-label">
                  <HistoryOutlined />
                  {language === "en" ? "Match History" : "对局记录"}
                </span>
              }
              key="matches"
            >
              <div className="match-history-card">
                {currentWallet ? (
                  <>
                    <div className="wallet-info">
                      {playerMatches.length > 0 && (
                        <div className="nova-section">
                          <div className="chart-container">
                            <Line
                              data={getMmrChartData()}
                              xField="match"
                              yField="mmr"
                              point={{
                                size: 5,
                                shape: "diamond",
                              }}
                              color="#9d00ff"
                              smooth={true}
                              xAxis={{
                                title: {
                                  text: language === "en" ? "Match" : "对局",
                                },
                              }}
                              yAxis={{
                                title: {
                                  text: "MMR",
                                },
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <Table
                        columns={matchColumns}
                        dataSource={playerMatches}
                        rowKey="matchId"
                        loading={matchLoading}
                        pagination={false}
                        className="match-table"
                        locale={{
                          emptyText: (
                            <Empty
                              description={
                                language === "en" ? "No match records" : "暂无对局记录"
                              }
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                          ),
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="select-player-prompt">
                    <Title level={4} style={{ color: "#fff" }}>
                      {language === "en"
                        ? "No match records"
                        : "您暂无对局记录"}
                    </Title>
                  </div>
                )}
              </div>
            </TabPane>
          </Tabs>
        </div>

        <Footer language={language} setLanguage={setLanguage} />
      </div>
      {/* 查看玩家对局记录的 Modal */}
      <CustomModal
        title={
          <span className="modal-match-title">
            {modalWallet ? `${modalWallet.slice(0, 6)}...${modalWallet.slice(-4)} 的对局记录` : ''}
          </span>
        }
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
      >
        {modalLoading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : modalMatches.length ? (
          <>
            <Title level={3} className="match-history-title modal-history-title">
              {language === "en" ? "Match History" : "我的对局记录"}
            </Title>
            <Table
              columns={matchColumns}
              dataSource={modalMatches}
              rowKey="matchId"
              loading={modalLoading}
              pagination={{ pageSize: 5 }}
              className="match-table"
              locale={{
                emptyText: (
                  <Empty
                    description={
                      language === "en" ? "No match records" : "暂无对局记录"
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </>
        ) : (
          <div className="empty-container">
            <Empty
              description={
                language === "en" ? "No match records" : "暂无对局记录"
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default LeaderboardPage;
